import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usePreferenceStore } from '@/lib/store/preferenceStore';
import Card from '@/components/common/Card';
import Button from '@/components/common/Button';
import Input from '@/components/common/Input';
import { Switch } from '@/components/common/Switch';
import Alert from '@/components/common/Alert';

const notificationPreferencesSchema = z.object({
  email_notifications: z.boolean(),
  schedule_publication_notification: z.boolean(),
  preference_submission_reminder: z.boolean(),
  shift_trade_notifications: z.boolean(),
  advance_schedule_reminder_days: z.number()
    .int()
    .min(0, { message: "Days must be 0 or greater" })
    .max(30, { message: "Days cannot exceed 30" })
});

type NotificationPreferencesFormValues = z.infer<typeof notificationPreferencesSchema>;

export const NotificationPreferencesForm = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const { teamPreferences, isLoading, error, fetchTeamPreferences, updateNotificationPreferences } = usePreferenceStore();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<NotificationPreferencesFormValues>({
    resolver: zodResolver(notificationPreferencesSchema),
    defaultValues: {
      email_notifications: true,
      schedule_publication_notification: true,
      preference_submission_reminder: true,
      shift_trade_notifications: true,
      advance_schedule_reminder_days: 7
    }
  });
  
  // For toggling switches
  const emailNotifications = watch('email_notifications');
  const schedulePublicationNotification = watch('schedule_publication_notification');
  const preferenceSubmissionReminder = watch('preference_submission_reminder');
  const shiftTradeNotifications = watch('shift_trade_notifications');
  
  useEffect(() => {
    if (teamId) {
      fetchTeamPreferences(teamId).then(preferences => {
        if (preferences?.notification_preferences) {
          const np = preferences.notification_preferences;
          setValue('email_notifications', np.email_notifications);
          setValue('schedule_publication_notification', np.schedule_publication_notification);
          setValue('preference_submission_reminder', np.preference_submission_reminder);
          setValue('shift_trade_notifications', np.shift_trade_notifications);
          setValue('advance_schedule_reminder_days', np.advance_schedule_reminder_days);
        }
      });
    }
  }, [teamId, fetchTeamPreferences, setValue]);
  
  const onSubmit = async (data: NotificationPreferencesFormValues) => {
    if (!teamId) return;
    
    setIsSubmitting(true);
    setSuccessMessage(null);
    
    try {
      await updateNotificationPreferences(teamId, data);
      setSuccessMessage('Notification preferences updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err) {
      console.error('Failed to update preferences:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  if (isLoading && !teamPreferences) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-primary rounded-full"></div>
      </div>
    );
  }
  
  return (
    <Card className="p-6">
      <h2 className="text-xl font-semibold mb-4">Notification Preferences</h2>
      
      {error && (
        <Alert variant="error" className="mb-4">
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert variant="success" className="mb-4">
          {successMessage}
        </Alert>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium">Email Notifications</h3>
              <p className="text-sm text-gray-500">
                Enable email notifications for all team events
              </p>
            </div>
            <Switch
              checked={emailNotifications}
              onChange={() => setValue('email_notifications', !emailNotifications)}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium">Schedule Publication Notification</h3>
              <p className="text-sm text-gray-500">
                Notify all providers when a new schedule is published
              </p>
            </div>
            <Switch
              checked={schedulePublicationNotification}
              onChange={() => setValue('schedule_publication_notification', !schedulePublicationNotification)}
              disabled={!emailNotifications}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium">Preference Submission Reminder</h3>
              <p className="text-sm text-gray-500">
                Send reminders to providers who haven't submitted preferences
              </p>
            </div>
            <Switch
              checked={preferenceSubmissionReminder}
              onChange={() => setValue('preference_submission_reminder', !preferenceSubmissionReminder)}
              disabled={!emailNotifications}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-medium">Shift Trade Notifications</h3>
              <p className="text-sm text-gray-500">
                Notify providers about shift trade requests and approvals
              </p>
            </div>
            <Switch
              checked={shiftTradeNotifications}
              onChange={() => setValue('shift_trade_notifications', !shiftTradeNotifications)}
              disabled={!emailNotifications}
            />
          </div>
          
          <Input
            type="number"
            label="Advance Schedule Reminder (Days)"
            {...register('advance_schedule_reminder_days', { valueAsNumber: true })}
            error={errors.advance_schedule_reminder_days?.message}
            helperText="Days before schedule start to send a reminder"
            disabled={!emailNotifications}
            required
          />
        </div>
        
        <div className="pt-4 border-t flex justify-end">
          <Button
            type="submit"
            variant="primary"
            isLoading={isSubmitting}
            disabled={!emailNotifications}
          >
            Save Preferences
          </Button>
        </div>
      </form>
    </Card>
  );
}; 